import { z } from 'zod';

/**
 * Maximum number of reviews returned in the `reviews[]` array.
 * The `reviewCount` field reflects the TRUE total (may exceed this cap).
 * Guard: keeps display + JSON-LD payload size bounded.
 */
export const MAX_REVIEWS = 20;

// A single product review as displayed on the PDP and emitted into JSON-LD.
// Fields chosen for what a review-display realistically needs AND what an
// a-manager checkidreview-style export plausibly returns.
export const ProductReviewSchema = z.object({
  /** Reviewer display name. Required (schema.org Review.author needs a name). */
  author: z.string().min(1).max(120),
  /** Integer star rating 1..5. */
  rating: z.number().int().min(1).max(5),
  /** Free-text review body. May be empty string (rating-only review). */
  body: z.string().max(5000).default(''),
  /** Optional short headline. */
  title: z.string().max(200).optional(),
  /** Optional ISO-8601 date the review was published. */
  date: z.string().datetime().optional(),
});
export type ProductReview = z.infer<typeof ProductReviewSchema>;

// The aggregate + list response for one product.
// EMPTY shape = { ratingValue: null, reviewCount: 0, reviews: [] }.
export const ProductReviewsResponseSchema = z.object({
  /**
   * Average rating across `reviews`, rounded to ≤1 decimal, as a NUMBER.
   * null when reviewCount === 0 (NEVER 0 — a 0 rating is invalid/penalising).
   */
  ratingValue: z.number().min(1).max(5).nullable(),
  /** Total review count (integer ≥ 0). */
  reviewCount: z.number().int().nonnegative(),
  /** Reviews, newest-first, capped at MAX_REVIEWS (see above). */
  reviews: z.array(ProductReviewSchema),
});
export type ProductReviewsResponse = z.infer<typeof ProductReviewsResponseSchema>;

/**
 * Compute ratingValue from an array of valid ProductReview items.
 * Returns null when the array is empty (never 0 — avoids Google penalty).
 * Single source of truth for avg computation shared by clients + tests.
 */
export function computeRatingValue(reviews: ProductReview[]): number | null {
  if (reviews.length === 0) return null;
  const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
  const avg = sum / reviews.length;
  // Round to 1 decimal: Math.round(avg * 10) / 10
  return Math.round(avg * 10) / 10;
}
